/**
 * Bidirectional file sync between local repo and cloud agent.
 *
 * - Server → Local: on `file_changed` events, write to local disk
 * - Local → Server: watch local repo with fs.watch, send `file_sync` messages
 *
 * Uses a suppression set to avoid echo loops (agent writes file → watcher
 * detects → would send back → suppressed).
 */

import * as fs from 'node:fs';
import * as path from 'node:path';
import { execFileSync } from 'node:child_process';
import type { AgentEvent, FileChangedEvent, ClientMessage } from '@spacecode/shared/agent';

/** How long to suppress a path after we wrote it (ms). */
const SUPPRESS_DURATION_MS = 2000;

export interface FileSyncOptions {
  /** Absolute path to the local repo root. */
  repoRoot: string;
  /** Send a message to the agent WebSocket. */
  send: (msg: ClientMessage) => void;
}

export class FileSync {
  private watcher: fs.FSWatcher | null = null;
  private suppressedPaths = new Map<string, number>();
  private options: FileSyncOptions;

  constructor(options: FileSyncOptions) {
    this.options = options;
  }

  /**
   * Bulk-sync tracked files from the local repo to the server.
   * Uses `git ls-files` to find tracked files, reads each, and sends file_sync messages.
   * Called once on session start so the agent's D1 FileStore has the full repo.
   */
  syncInitial(): number {
    try {
      const stdout = execFileSync('git', ['ls-files'], {
        cwd: this.options.repoRoot,
        encoding: 'utf-8',
        timeout: 10_000,
      });
      const files = stdout.trim().split('\n').filter((f) => f && !this.shouldIgnore(f));
      // Cap at 300 files to avoid flooding the WebSocket on large repos
      const batch = files.slice(0, 300);
      let synced = 0;
      for (const filePath of batch) {
        const absPath = path.join(this.options.repoRoot, filePath);
        try {
          const stat = fs.statSync(absPath);
          // Skip large files (> 256KB)
          if (stat.size > 256 * 1024) continue;
          const content = fs.readFileSync(absPath, 'utf-8');
          this.options.send({ type: 'file_sync', path: filePath, content });
          synced++;
        } catch {
          // Skip unreadable files
        }
      }
      return synced;
    } catch {
      // git ls-files failed — not a git repo or git not available
      return 0;
    }
  }

  /** Start watching the local repo for changes. */
  start(): void {
    if (this.watcher) return;

    this.watcher = fs.watch(
      this.options.repoRoot,
      { recursive: true },
      (eventType, filename) => {
        if (!filename) return;
        this.handleLocalChange(filename);
      },
    );
  }

  /** Stop the watcher and clean up. */
  stop(): void {
    this.watcher?.close();
    this.watcher = null;
    this.suppressedPaths.clear();
  }

  /** Handle a file_changed event from the agent — write to local disk. */
  handleServerEvent(event: AgentEvent): void {
    if (event.type !== 'file_changed') return;
    const fileEvent = event as FileChangedEvent;
    const absPath = path.join(this.options.repoRoot, fileEvent.path);

    // Suppress echo from watcher
    this.suppressedPaths.set(fileEvent.path, Date.now() + SUPPRESS_DURATION_MS);

    if (fileEvent.status === 'deleted') {
      try { fs.unlinkSync(absPath); } catch { /* may not exist */ }
    } else {
      // Ensure parent directory exists
      const dir = path.dirname(absPath);
      fs.mkdirSync(dir, { recursive: true });
      fs.writeFileSync(absPath, fileEvent.content, 'utf-8');
    }
  }

  private handleLocalChange(relativePath: string): void {
    // Normalize path separators
    const normalized = relativePath.replace(/\\/g, '/');

    // Skip generated/ignored paths
    if (this.shouldIgnore(normalized)) return;

    // Check suppression (was this written by us from a server event?)
    const suppressUntil = this.suppressedPaths.get(normalized);
    if (suppressUntil && Date.now() < suppressUntil) {
      return;
    }
    this.suppressedPaths.delete(normalized);

    // Read file content and send to server
    const absPath = path.join(this.options.repoRoot, normalized);
    try {
      const content = fs.readFileSync(absPath, 'utf-8');
      this.options.send({
        type: 'file_sync',
        path: normalized,
        content,
      });
    } catch {
      // File may have been deleted — send empty content
      // (the server will handle it as a deletion if needed)
    }
  }

  private shouldIgnore(p: string): boolean {
    const ignored = [
      'node_modules/',
      '.git/',
      'dist/',
      '.cache/',
      '.next/',
      '__pycache__/',
      '.pytest_cache/',
      'coverage/',
      '.DS_Store',
    ];
    return ignored.some((i) => p.includes(i));
  }
}
